import type { GameContext } from '../core/context';
import type { System } from '../core/system';
import { FIXED_DT } from '../core/loop';
import { ViewTester } from '../traffic/visibility';
import { PedSim } from './pedSim';
import { PedView } from './pedView';

/** Focus movement in one step (m) treated as a teleport. */
const FOCUS_JUMP = 50;

/**
 * Pedestrians (order 245: right after civilian traffic, before the vehicle system). Owns the
 * `peds` service, the instanced view and the pedestrian test hooks.
 */
export function createPedSystem(): System {
  let ctx: GameContext;
  let sim: PedSim;
  let view: PedView | null = null;
  let avgMs = 0;
  let lastFocus: { x: number; z: number } | null = null;
  const offs: (() => void)[] = [];

  return {
    id: 'peds',
    order: 245,
    init(c) {
      ctx = c;
      const layout = c.services.get('layout');
      const grid = c.services.get('vehicleGrid');
      const focus = (): { x: number; z: number } => (sim ? sim.focus() : layout.playerSpawn.pos);
      sim = new PedSim(c, layout, grid, new ViewTester(c, focus));
      c.services.provide('peds', sim);
      view = new PedView(c.scene);

      offs.push(
        c.events.on('pedPanic', (e) => sim.panic(e.pos, e.radius)),
        c.events.on('vehicleDestroyed', (e) => sim.panic(e.pos, 28)),
        // The explosion (FIRE_FUSE later) scatters everyone again, idle groups included, wider.
        c.events.on('vehicleExploded', (e) => sim.panic(e.pos, 40)),
        c.events.on('crime', (e) => {
          // Violence scatters the crowd; petty stuff doesn't.
          if (e.byPlayer && (e.type === 'assault' || e.type === 'robbery' || e.type === 'destruction' || e.type === 'hitPolice')) sim.panic(e.pos, 20);
        }),
        c.events.on('driverEjected', (e) => {
          sim.spawnFleeing({ x: e.pos.x, z: e.pos.z }, c.services.tryGet('player')?.pos ?? null);
        }),
      );

      c.hooks.register('setPedDensity', (d) => {
        sim.density = Math.max(0, d);
        return sim.target;
      });
      c.hooks.register('inspectPeds', () => sim.inspect());
      c.hooks.register('crowdBodies', () => view?.bodies() ?? {});
      c.hooks.register('crowdCost', () => view?.cost() ?? { calls: 0, tris: 0, shadowCalls: 0, shadowTris: 0, meshy: 0 });
      c.hooks.register('spawnPedAt', (x, z) => sim.spawn(x, z)?.id ?? -1);
      c.hooks.register('panicPeds', (x, z, radius) => sim.panic({ x, z }, radius));
      c.hooks.registerSnapshot('peds', () => {
        const states: Record<string, number> = {};
        for (const p of sim.list) states[p.state] = (states[p.state] ?? 0) + 1;
        return { active: sim.active, target: sim.target, density: sim.density, states, spawned: sim.stats.spawned, despawned: sim.stats.despawned, hits: sim.stats.hits, poolCreated: sim.stats.poolCreated, poolInUse: sim.stats.poolInUse };
      });
    },
    start() {
      sim.fillInitial();
    },
    fixedUpdate(dt) {
      const t0 = performance.now();
      // A big jump of the focus (teleport, busted/wasted respawn, mission warp): nobody saw the old
      // pavements empty, so drop the far crowd and repopulate around the new spot at once (as traffic does).
      const f = sim.focus();
      if (lastFocus && Math.hypot(f.x - lastFocus.x, f.z - lastFocus.z) > FOCUS_JUMP) {
        sim.releaseFar();
        sim.fillInitial();
      }
      lastFocus = { x: f.x, z: f.z };
      sim.fixedUpdate(dt);
      avgMs += (performance.now() - t0 - avgMs) * 0.02;
      sim.stats.pedMs = avgMs;
      sim.stats.poolCreated = sim.poolCreated;
      sim.stats.poolInUse = sim.poolInUse;
    },
    update(_dt, alpha) {
      view?.update(sim.list, alpha, ctx.state.time + alpha * FIXED_DT);
    },
    dispose() {
      for (const off of offs) off();
      offs.length = 0;
      view?.dispose();
      view = null;
      sim.dispose();
      void ctx;
    },
  };
}
